import { Badge } from "@/components/ui/baseUi/badge"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoxOfferFormattedPrice } from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferItemEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { getOfferTypeIcon, getOfferVisibility } from "@modules/boxOffer/utils/boxOfferUtils"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { ClockFadingIcon, HandshakeIcon, Tally5Icon, UsersRoundIcon } from "lucide-react"

type BoxOfferCardProps = {
  offer: BoxOfferItemEntity
  discountPrice?: PriceObjectEntity
} & React.ComponentProps<"div">

export function BoxOfferCard({ offer, discountPrice, className, ...props }: BoxOfferCardProps) {
  const commitmentPeriod = offer.getCommitmentPeriodText()
  const validityPeriod = offer.getValidityPeriodText()
  const sessionCount = offer.getSessionCountText()
  const rules = offer.getReservationRulesText()
  const visibilityStatus = getOfferVisibility(offer, "var(--textDefault-caption)")

  return (
    <div
      className={cn(
        "flex w-full cursor-pointer justify-between gap-4 rounded-xl border border-borderDefault p-4",
        className,
      )}
      {...props}
    >
      <div className="flex w-7/12 flex-col justify-between gap-3">
        <div className="flex flex-col">
          <Text fontStyle="TM" className="wrap-anywhere">
            {offer.name}
          </Text>
          {rules.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {rules.map((rule, index) => (
                <div key={index} className="py-1">
                  <Text fontStyle="BS" className="text-primary-700" style={{ fontSize: "10px", fontStyle: "italic" }}>
                    {rule}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {commitmentPeriod && (
            <div className="flex items-center gap-2">
              <HandshakeIcon size={16} className="shrink-0 text-neutral-600" />
              <Text fontStyle="BS" className="text-neutral-600">
                {commitmentPeriod}
              </Text>
            </div>
          )}

          {validityPeriod && (
            <div className="flex items-center gap-2">
              <ClockFadingIcon size={16} className="shrink-0 text-neutral-600" />
              <Text fontStyle="BS" className="text-neutral-600">
                {validityPeriod}
              </Text>
            </div>
          )}

          {sessionCount && (
            <div className="flex items-center gap-2">
              <Tally5Icon size={16} className="shrink-0 text-neutral-600" />
              <Text fontStyle="BS" className="text-neutral-600">
                {sessionCount}
              </Text>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="-ml-px flex h-4.5 w-4.5 shrink-0 items-center">{visibilityStatus.icon}</div>
            <Text fontStyle="BS" className="text-neutral-600">
              {visibilityStatus.text}
            </Text>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="neutralSubtle" size="lg" className="leading-normal">
            <UsersRoundIcon size={16} />
            {offer.activeMembers}
          </Badge>
          <Badge variant="primarySubtle" size="lg" className="leading-normal">
            {getOfferTypeIcon(offer.type, "white", 24)}
            {offer.getOfferTypeLabel()}
          </Badge>
        </div>

        <div className="flex flex-col items-end pr-2">
          {discountPrice && (
            <BoxOfferFormattedPrice
              price={offer.price}
              showRecurringPeriod={offer.type === BoxOfferType.Subscription}
              recurringPeriod={
                offer.type === BoxOfferType.Subscription && "recurringPeriod" in offer
                  ? offer.recurringPeriod
                  : undefined
              }
              periodStyle="TS"
              lineThrough={true}
              wholePartStyle="BS"
              decimalPartStyle="BS"
            />
          )}
          <BoxOfferFormattedPrice
            price={discountPrice ?? offer.price}
            showRecurringPeriod={offer.type === BoxOfferType.Subscription}
            recurringPeriod={
              offer.type === BoxOfferType.Subscription && "recurringPeriod" in offer ? offer.recurringPeriod : undefined
            }
            periodStyle="TS"
          />
        </div>
      </div>
    </div>
  )
}
